import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, ChevronRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import Testimonials from "@/components/Testimonials";
import { KEYWORDS, FAQ } from "@/lib/seoContent";
import {
  organizationLd,
  websiteLd,
  professionalServiceLd,
  webPageLd,
  breadcrumbLd,
  faqPageLd,
  reviewLd,
} from "@/lib/structuredData";

const HERO_ABSTRACT =
  "https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMG1pbmltYWxpc3R8ZW58MHx8fHwxNzgyOTM3NTMwfDA&ixlib=rb-4.1.0&q=85";

const ZEMKA_IMG =
  "https://customer-assets.emergentagent.com/job_vibe-showcase-4/artifacts/i6afm4d1_1d5966b5-cb88-432d-95ce-833443653f42.png";

const Home = () => {
  const { t, lang } = useApp();
  useSEO({
    title: t.home.metaTitle,
    description: t.home.metaDesc,
    path: "/",
    lang,
    keywords: KEYWORDS[lang].home,
    jsonLd: [
      organizationLd(),
      websiteLd(),
      professionalServiceLd(),
      webPageLd({
        path: "/",
        name: t.home.metaTitle,
        description: t.home.metaDesc,
        lang,
      }),
      breadcrumbLd([{ name: t.nav.home, path: "/" }]),
      reviewLd(),
      faqPageLd(FAQ[lang]),
    ],
  });

  return (
    <main data-testid="home-page" className="pt-16 lg:pt-20">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-[0.35]">
          <img
            src={HERO_ABSTRACT}
            alt="Minimalist modern architecture — abstract inspiration for professional business websites"
            className="w-full h-full object-cover"
            loading="eager"
            fetchpriority="high"
            width="1920"
            height="1080"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/60 to-white" />
        </div>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-24 lg:pb-40">
          <div className="flex items-center gap-3 text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">
            <Sparkles className="h-3.5 w-3.5 text-[#3A00FF]" aria-hidden />
            {t.home.eyebrow}
          </div>

          <h1
            data-testid="hero-title"
            className="mt-8 max-w-[16ch] text-[13vw] sm:text-6xl md:text-7xl lg:text-[8.5rem] xl:text-[10rem] leading-[0.9] tracking-tighter font-black text-[#0A0A0A] font-[Cabinet_Grotesk]"
          >
            {t.home.heroTitleA}
            <br />
            <span className="italic font-black text-[#3A00FF]">
              {t.home.heroTitleB}
            </span>
          </h1>

          <p className="mt-8 max-w-2xl text-base sm:text-lg text-[#525252] leading-relaxed">
            {t.home.heroLead}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/contact">
              <Button
                data-testid="hero-cta-primary"
                className="h-12 px-6 rounded-none bg-[#0A0A0A] text-white hover:bg-[#3A00FF] text-xs tracking-widest uppercase font-bold shadow-[4px_4px_0px_#3A00FF] hover:shadow-[6px_6px_0px_#3A00FF] transition-all"
              >
                {t.home.heroPrimary}
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/portfolio">
              <Button
                data-testid="hero-cta-secondary"
                variant="ghost"
                className="h-12 px-6 rounded-none border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-xs tracking-widest uppercase font-bold"
              >
                {t.home.heroSecondary}
              </Button>
            </Link>
          </div>

          <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 border-y border-[#0A0A0A]">
            {t.home.metrics.map((m, i) => (
              <div
                key={i}
                className={`p-6 lg:p-8 ${
                  i < t.home.metrics.length - 1 ? "lg:border-r" : ""
                } ${i < 2 ? "border-b lg:border-b-0" : ""} ${
                  i % 2 === 0 ? "border-r lg:border-r" : ""
                } border-[#0A0A0A]/20`}
              >
                <div className="text-3xl lg:text-4xl font-black tracking-tight font-[Cabinet_Grotesk]">
                  {m.k}
                </div>
                <div className="mt-1 text-xs tracking-[0.15em] uppercase text-[#525252]">
                  {m.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE PROP */}
      <section className="border-t border-[#0A0A0A] bg-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">
            <span className="inline-block h-[1px] w-8 bg-[#3A00FF] align-middle mr-3" />
            Value proposition
          </div>
          <div className="lg:col-span-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight font-black text-[#0A0A0A] font-[Cabinet_Grotesk]">
              {t.home.valuePropTitle}
            </h2>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-[#525252] leading-relaxed">
              {t.home.valuePropBody}
            </p>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="border-t border-[#0A0A0A] bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
          <div className="flex items-center gap-3 text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">
            <span className="inline-block h-[1px] w-8 bg-[#3A00FF]" />
            {t.home.featuresEyebrow}
          </div>
          <h2 className="mt-4 max-w-3xl text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight font-black text-[#0A0A0A] font-[Cabinet_Grotesk]">
            {t.home.featuresTitle}
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#0A0A0A]/20">
            {t.home.features.map((f, i) => (
              <article
                key={i}
                data-testid={`feature-card-${i}`}
                className="group relative p-8 lg:p-10 border-r border-b border-[#0A0A0A]/20 bg-white hover:bg-[#0A0A0A] hover:text-white transition-colors duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="text-xs tracking-[0.2em] uppercase font-bold opacity-60 group-hover:opacity-100">
                    0{i + 1}
                  </div>
                  <ChevronRight className="h-4 w-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </div>
                <h3 className="mt-6 text-xl lg:text-2xl tracking-tight font-bold font-[Cabinet_Grotesk]">
                  {f.t}
                </h3>
                <p className="mt-3 text-sm leading-relaxed opacity-80">{f.d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WORK TEASER */}
      <section className="border-t border-[#0A0A0A] bg-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">
                <span className="inline-block h-[1px] w-8 bg-[#3A00FF]" />
                {t.home.workEyebrow}
              </div>
              <h2 className="mt-4 max-w-2xl text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight font-black text-[#0A0A0A] font-[Cabinet_Grotesk]">
                {t.home.workTitle}
              </h2>
              <p className="mt-4 max-w-xl text-base text-[#525252]">
                {t.home.workLead}
              </p>
            </div>
            <Link to="/portfolio" data-testid="home-portfolio-cta">
              <Button
                variant="ghost"
                className="h-11 px-5 rounded-none border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-xs tracking-widest uppercase font-bold"
              >
                {t.home.workCta}
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <Link
              to="/portfolio"
              className="lg:col-span-8 group relative overflow-hidden border border-[#0A0A0A]"
              data-testid="home-featured-work"
            >
              <div className="aspect-[16/10] overflow-hidden bg-[#0F2A44]">
                <img
                  src={ZEMKA_IMG}
                  alt="Zemka Gebäudereinigung — professional business website case study by NetoVibe"
                  className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-6 lg:p-8 flex items-end justify-between gap-6">
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#3A00FF]">
                    Featured
                  </div>
                  <h3 className="mt-2 text-2xl lg:text-3xl font-black tracking-tight font-[Cabinet_Grotesk]">
                    Zemka Gebäudereinigung
                  </h3>
                  <p className="mt-2 text-sm text-[#525252]">
                    Local cleaning & janitorial · Professional Business package
                  </p>
                </div>
                <ArrowUpRight className="h-6 w-6 shrink-0 group-hover:text-[#3A00FF] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
              </div>
            </Link>

            <div className="lg:col-span-4 grid grid-cols-1 gap-6">
              <div className="border border-[#0A0A0A] p-6 lg:p-8 bg-[#0A0A0A] text-white">
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#3A00FF]">
                  Now booking
                </div>
                <div className="mt-4 text-3xl font-black tracking-tight font-[Cabinet_Grotesk]">
                  Q1 launches.
                </div>
                <p className="mt-3 text-sm text-white/70">
                  Two slots remaining for the current sprint window.
                </p>
                <Link to="/contact" className="mt-6 inline-flex items-center gap-2 text-xs tracking-widest uppercase font-bold hover:text-[#3A00FF]">
                  Claim a slot <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="border border-[#0A0A0A] p-6 lg:p-8">
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">
                  Focus
                </div>
                <ul className="mt-4 space-y-2 text-sm text-[#525252]">
                  <li>· Local business websites</li>
                  <li>· Small corporate rebrands</li>
                  <li>· Bilingual EN / DE launches</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials
        eyebrow={t.home.testimonialsEyebrow}
        title={t.home.testimonialsTitle}
      />

      {/* CTA */}
      <section className="border-t border-[#0A0A0A] bg-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter font-black text-[#0A0A0A] font-[Cabinet_Grotesk]">
              {t.home.ctaTitle}
            </h2>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-[#525252]">
              {t.home.ctaLead}
            </p>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Link to="/contact">
              <Button
                data-testid="home-final-cta"
                className="h-14 px-8 rounded-none bg-[#3A00FF] text-white hover:bg-[#0A0A0A] text-xs tracking-widest uppercase font-bold shadow-[4px_4px_0px_#0A0A0A] hover:shadow-[6px_6px_0px_#0A0A0A] transition-all"
              >
                {t.nav.cta}
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
